import React, { useMemo } from 'react';
import { Feature, isAvailable } from 'App/services/FeatureHelperStandalone';
import { ButtonSelect, Flex } from '@upsales/components';
import T from 'Components/Helpers/translate';
import type {
	ActiveFilter,
	ProductTypeFilter,
	RecurringFilter,
	CustomFieldFilters,
	StandardFieldFilters
} from 'Store/reducers/AdminProductListReducer';
import ListViewFilters from 'App/components/ListView/ListViewFilters';
import { ProductCustomField } from 'App/resources/Model/CustomField';
import { getCustomFieldConfig, isCustom } from 'App/helpers/filterHelper';
import { CustomFieldFilterConfig, FilterConfig } from 'App/babel/filterConfigs/FilterConfig';
import { useSoftDeployAccess } from 'App/components/hooks';
import { ListViewFilter } from 'App/resources/AllIWant';
import getProductFilterConfigs from 'App/babel/filterConfigs/Product';
import AppService from 'App/legacy/AppService';

type Props = {
	activeFilter: ActiveFilter;
	onActiveFilterChange: (value: ActiveFilter) => void;
	productTypeFilter: ProductTypeFilter;
	onProductTypeFilterChange: (value: ProductTypeFilter) => void;
	recurringFilter: RecurringFilter;
	onRecurringFilterChange: (value: RecurringFilter) => void;
	customFieldFilters?: CustomFieldFilters;
	onCustomFieldFiltersChange?: (filters: CustomFieldFilters) => void;
	standardFieldFilters?: StandardFieldFilters;
	onStandardFieldFiltersChange?: (filters: StandardFieldFilters) => void;
};

const getCustomFieldFilterConfigs = (customFields: ProductCustomField[]) => {
	return customFields.reduce<{ [filterName: string]: CustomFieldFilterConfig }>((res, customField) => {
		const filterName = `product.Custom_${customField.id}`;
		res[filterName] = getCustomFieldConfig({
			entityType: 'product',
			name: filterName,
			field: customField,
			fieldId: customField.id
		});
		return res;
	}, {});
};

// ListViewFilters re-emits unchanged filters by reference, so a shallow
// key + reference comparison is enough to tell which slice changed.
const filtersDiffer = (
	next: { [filterName: string]: ListViewFilter },
	current: { [filterName: string]: ListViewFilter }
) => {
	const nextKeys = Object.keys(next);
	return nextKeys.length !== Object.keys(current).length || nextKeys.some(key => next[key] !== current[key]);
};

const ProductListFilters = ({
	activeFilter,
	onActiveFilterChange,
	productTypeFilter,
	onProductTypeFilterChange,
	recurringFilter,
	onRecurringFilterChange,
	customFieldFilters = {},
	onCustomFieldFiltersChange,
	standardFieldFilters = {},
	onStandardFieldFiltersChange
}: Props) => {
	const hasProductBundle = isAvailable(Feature.PRODUCT_BUNDLES);
	const hasRecurringOrder = isAvailable(Feature.RECURRING_ORDER);
	const hasStandardFieldFilter = useSoftDeployAccess('PRODUCT_LIST_STANDARD_FIELD_FILTER');

	const customFields = useMemo(() => {
		return (AppService.getCustomFields('product') as ProductCustomField[]).filter(
			cf => cf.$hasAccess && cf.visible
		);
	}, []);

	const customFieldFilterConfigs = useMemo(() => getCustomFieldFilterConfigs(customFields), [customFields]);
	const standardFieldFilterConfigs = useMemo(
		() => (hasStandardFieldFilter ? getProductFilterConfigs() : {}),
		[hasStandardFieldFilter]
	);

	// Merge configs so the drawer shows standard + custom in one section.
	const filterConfigs = useMemo<{ [name: string]: FilterConfig }>(
		() => ({ ...standardFieldFilterConfigs, ...customFieldFilterConfigs }),
		[standardFieldFilterConfigs, customFieldFilterConfigs]
	);

	const activeFilters = useMemo(
		() => ({ ...standardFieldFilters, ...customFieldFilters }),
		[standardFieldFilters, customFieldFilters]
	);

	const hasChanged = Object.keys(customFieldFilters).length > 0 || Object.keys(standardFieldFilters).length > 0;

	const showDrawer =
		(customFields.length > 0 && !!onCustomFieldFiltersChange) ||
		(hasStandardFieldFilter && !!onStandardFieldFiltersChange);

	const onDrawerChange = (filters: { [filterName: string]: ListViewFilter }) => {
		const nextCustom: CustomFieldFilters = {};
		const nextStandard: StandardFieldFilters = {};
		Object.keys(filters).forEach(filterName => {
			if (isCustom(filterName)) {
				nextCustom[filterName] = filters[filterName];
			} else {
				nextStandard[filterName] = filters[filterName];
			}
		});
		// Each callback triggers a table + count refetch, so only dispatch the slice
		// that actually changed — dispatching both fires duplicate requests where the
		// first pair is built from stale state and can race past the fresh one.
		if (filtersDiffer(nextCustom, customFieldFilters)) {
			onCustomFieldFiltersChange?.(nextCustom);
		}
		if (filtersDiffer(nextStandard, standardFieldFilters)) {
			onStandardFieldFiltersChange?.(nextStandard);
		}
	};

	return (
		<Flex className="admin-products-filters" gap="16px" alignItems="center">
			{hasProductBundle ? (
				<ButtonSelect
					size="sm"
					value={productTypeFilter}
					options={[
						{ value: 'all' as const, title: T('default.all') },
						{ value: 'products' as const, title: T('default.products') },
						{ value: 'bundles' as const, title: T('admin.products.bundles') }
					]}
					onChange={onProductTypeFilterChange}
				/>
			) : null}
			{hasRecurringOrder ? (
				<ButtonSelect
					size="sm"
					value={recurringFilter}
					options={[
						{ value: 'all' as const, title: T('default.all') },
						{ value: 'oneoff' as const, title: T('admin.products.oneOff') },
						{ value: 'recurring' as const, title: T('admin.products.recurring') }
					]}
					onChange={onRecurringFilterChange}
				/>
			) : null}
			<ButtonSelect
				size="sm"
				value={activeFilter}
				options={[
					{ value: 'all' as const, title: T('default.all') },
					{ value: 'active' as const, title: T('default.active') },
					{ value: 'inactive' as const, title: T('default.inactive') }
				]}
				onChange={onActiveFilterChange}
			/>
			{showDrawer ? (
				<ListViewFilters
					filterConfigs={filterConfigs}
					activeFilters={activeFilters}
					onChange={onDrawerChange}
					hasChanged={hasChanged}
					customFields={customFields}
					listType="product"
					opts={{ noTabs: true, alwaysShowButton: true }}
				/>
			) : null}
		</Flex>
	);
};

export default ProductListFilters;
